import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Grid
} from '@mui/material';
import {
  Visibility,
  Close,
  Assignment,
  Send,
  AttachFile,
  PriorityHigh,
  Search,
  FilterList
} from '@mui/icons-material';
import axios from 'axios';

interface SupportTicket {
  id: string;
  ticketNumber: string;
  reporterName: string;
  reporterEmail: string;
  reporterPhone: string;
  reportedUserId?: string;
  reportedUserName?: string;
  category: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  assignedTo?: string;
  assignedToName?: string;
  attachments?: string[];
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  responses: TicketResponse[];
}

interface TicketResponse {
  id: string;
  message: string;
  isInternal: boolean;
  authorName: string;
  createdAt: string;
}

interface MessageReport {
  id: string;
  status: 'PENDING' | 'UNDER_REVIEW' | 'RESOLVED' | 'DISMISSED';
  reason: string;
  additionalInfo?: string;
  createdAt: string;
  message: {
    content: string;
    createdAt: string;
  };
  reporter: {
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
  };
  reportedUser: {
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
  };
}

const SupportPage: React.FC = () => {
  const [mainTab, setMainTab] = useState(0);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [reports, setReports] = useState<MessageReport[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [filteredReports, setFilteredReports] = useState<MessageReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    pendingReports: 0
  });

  // Ticket Details Dialog
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [selectedReport, setSelectedReport] = useState<MessageReport | null>(null);
  const [linkedSupportTicket, setLinkedSupportTicket] = useState<SupportTicket | null>(null);
  const [reportDetailsOpen, setReportDetailsOpen] = useState(false);
  const [mediationMessage, setMediationMessage] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Assignment Dialog
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [supportAgents, setSupportAgents] = useState<any[]>([]);

  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchTickets();
    fetchReports();
    fetchSupportAgents();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tickets, reports, statusFilter, categoryFilter, priorityFilter, searchQuery, mainTab]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTickets(response.data.tickets || []);

      // Update stats
      const allTickets = response.data.tickets || [];
      setStats(prev => ({
        ...prev,
        total: allTickets.length,
        new: allTickets.filter((t: SupportTicket) => t.status === 'new').length,
        inProgress: allTickets.filter((t: SupportTicket) => t.status === 'in_progress').length,
        resolved: allTickets.filter((t: SupportTicket) => t.status === 'resolved').length,
        closed: allTickets.filter((t: SupportTicket) => t.status === 'closed').length
      }));
    } catch (error: any) {
      console.error('Failed to fetch tickets:', error);
      showAlert('error', error.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/message-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allReports = response.data.reports || [];
      setReports(allReports);
      setStats(prev => ({
        ...prev,
        pendingReports: allReports.filter((r: MessageReport) => r.status === 'PENDING').length
      }));
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  };

  const fetchSupportAgents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/support/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportAgents(response.data.agents || []);
    } catch (error) {
      console.error('Failed to fetch support agents:', error);
    }
  };

  const applyFilters = () => {
    if (mainTab === 0) {
      let filtered = [...tickets];
      if (statusFilter !== 'all') filtered = filtered.filter(t => t.status === statusFilter);
      if (categoryFilter !== 'all') filtered = filtered.filter(t => t.category === categoryFilter);
      if (priorityFilter !== 'all') filtered = filtered.filter(t => t.priority === priorityFilter);
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(t =>
          t.ticketNumber.toLowerCase().includes(query) ||
          t.subject.toLowerCase().includes(query) ||
          t.reporterName.toLowerCase().includes(query) ||
          t.reporterEmail.toLowerCase().includes(query)
        );
      }
      setFilteredTickets(filtered);
    } else {
      let filtered = [...reports];
      if (statusFilter !== 'all') filtered = filtered.filter(r => r.status === statusFilter.toUpperCase());
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(r =>
          r.reporter.firstName.toLowerCase().includes(query) ||
          r.reporter.lastName.toLowerCase().includes(query) ||
          r.reportedUser.firstName.toLowerCase().includes(query) ||
          r.reportedUser.lastName.toLowerCase().includes(query) ||
          r.reason.toLowerCase().includes(query)
        );
      }
      setFilteredReports(filtered);
    }
  };

  const handleOpenDetails = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setDetailsOpen(true);
    setActiveTab(0);
  };

  const handleOpenReportDetails = async (report: MessageReport) => {
    setSelectedReport(report);
    setReportDetailsOpen(true);
    setLinkedSupportTicket(null); // Reset previous

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/message-reports/${report.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.supportTicket) {
        setLinkedSupportTicket(response.data.supportTicket);
      }
    } catch (error) {
      console.error('Failed to fetch linked support ticket:', error);
    }
  };

  const handleSendMediationMessage = async () => {
    if (!linkedSupportTicket || !mediationMessage.trim()) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/support/tickets/${linkedSupportTicket.id}/mediation-chat`,
        { message: mediationMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Message sent to user\'s inbox');
      setMediationMessage('');
      
      // Refresh report details to show new message
      if (selectedReport) handleOpenReportDetails(selectedReport);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to send message');
    }
  };

  const handleCloseMediation = async (finalMessage: string) => {
    if (!linkedSupportTicket) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/support/tickets/${linkedSupportTicket.id}/close-mediation`,
        { finalMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Mediation case closed');
      setReportDetailsOpen(false);
      fetchReports();
      fetchTickets();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to close case');
    }
  };

  const handleUpdateReportStatus = async (reportId: string, status: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${apiUrl}/admin/message-reports/${reportId}`, {
        status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAlert('success', 'Report status updated');
      fetchReports();
      setReportDetailsOpen(false);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to update report');
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTicket(null);
    setResponseMessage('');
    setIsInternalNote(false);
  };

  const handleSendResponse = async () => {
    if (!selectedTicket || !responseMessage.trim()) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/support/tickets/${selectedTicket.id}/response`,
        {
          message: responseMessage,
          isInternal: isInternalNote
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', isInternalNote ? 'Internal note added' : 'Response sent to user');
      setResponseMessage('');
      fetchTickets();

      // Refresh ticket details
      const response = await axios.get(`${apiUrl}/admin/support/tickets/${selectedTicket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data.ticket);
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to send response');
    }
  };

  const handleAssignTicket = async () => {
    if (!selectedTicket || !assignTo) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/support/tickets/${selectedTicket.id}/assign`,
        { assignedTo: assignTo },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Ticket assigned successfully');
      setAssignmentOpen(false);
      fetchTickets();
      handleCloseDetails();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to assign ticket');
    }
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedTicket) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/support/tickets/${selectedTicket.id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Ticket status updated');
      fetchTickets();
      handleCloseDetails();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleUpdatePriority = async (priority: string) => {
    if (!selectedTicket) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${apiUrl}/admin/support/tickets/${selectedTicket.id}/priority`,
        { priority },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Priority updated');
      fetchTickets();

      // Update selected ticket
      setSelectedTicket({ ...selectedTicket, priority: priority as any });
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to update priority');
    }
  };

  const showAlert = (severity: 'success' | 'error' | 'info', message: string) => {
    setAlert({ show: true, message, severity });
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'new': return 'error';
      case 'in_progress': return 'primary';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const categories = [
    'User Complaint - Driver Behavior',
    'User Complaint - Passenger Behavior',
    'App Technical Issue',
    'Safety Concern',
    'Payment Problem',
    'Feature Request',
    'Other'
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Support & Moderation
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={mainTab} onChange={(e, newValue) => setMainTab(newValue)}>
          <Tab label="Support Tickets" icon={<Assignment />} iconPosition="start" />
          <Tab 
            label="Chat Reports" 
            icon={<Badge badgeContent={stats.pendingReports} color="error"><PriorityHigh /></Badge>} 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>

      {mainTab === 0 ? (
        <>
          {/* Stats Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Tickets
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  New
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {stats.new}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  In Progress
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {stats.inProgress}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Resolved
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.resolved}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Closed
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.closed}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <TextField
                placeholder="Search tickets..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Tickets Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket #</TableCell>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        #{ticket.ticketNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>{ticket.reporterName[0]}</Avatar>
                        <Box>
                          <Typography variant="body2">{ticket.reporterName}</Typography>
                          <Typography variant="caption" color="textSecondary">{ticket.reporterEmail}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{ticket.category}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ticket.subject}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority}
                        size="small"
                        color={getPriorityColor(ticket.priority)}
                        icon={ticket.priority === 'urgent' ? <PriorityHigh /> : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={ticket.status.replace('_', ' ')} size="small" color={getStatusColor(ticket.status)} />
                    </TableCell>
                    <TableCell>
                      {ticket.assignedToName ? (
                        <Chip label={ticket.assignedToName} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="textSecondary">Unassigned</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenDetails(ticket)}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredTickets.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />
          </TableContainer>
        </>
      ) : (
        <>
          {/* Chat Reports Section */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                placeholder="Search reports..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="dismissed">Dismissed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Reported User</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Typography variant="body2">{report.reporter.preferredName || report.reporter.firstName} {report.reporter.lastName ? report.reporter.lastName.charAt(0) + '.' : ''}</Typography>
                      <Typography variant="caption" color="textSecondary">{report.reporter.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{report.reportedUser.preferredName || report.reportedUser.firstName} {report.reportedUser.lastName ? report.reportedUser.lastName.charAt(0) + '.' : ''}</Typography>
                      <Typography variant="caption" color="textSecondary">{report.reportedUser.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {report.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={report.status} 
                        size="small" 
                        color={report.status === 'PENDING' ? 'error' : report.status === 'RESOLVED' ? 'success' : 'default'} 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{new Date(report.createdAt).toLocaleDateString()}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenReportDetails(report)}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredReports.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />
          </TableContainer>
        </>
      )}

      <Dialog open={reportDetailsOpen} onClose={() => setReportDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Chat Report & Mediation</Typography>
            {linkedSupportTicket && (
              <Chip 
                label={linkedSupportTicket.status.replace('_', ' ')} 
                color={getStatusColor(linkedSupportTicket.status)}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                {/* Report Summary */}
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Reporter</Typography>
                  <Typography variant="body1">{selectedReport.reporter.preferredName || selectedReport.reporter.firstName} {selectedReport.reporter.lastName ? selectedReport.reporter.lastName.charAt(0) + '.' : ''}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Reported User</Typography>
                  <Typography variant="body1">{selectedReport.reportedUser.preferredName || selectedReport.reportedUser.firstName} {selectedReport.reportedUser.lastName ? selectedReport.reportedUser.lastName.charAt(0) + '.' : ''}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="textSecondary">Reason</Typography>
                  <Typography variant="body2" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>{selectedReport.reason}</Typography>
                </Grid>

                {/* Legal Disclaimer */}
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" icon={false} sx={{ mt: 2, mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="h5" fontWeight="bold" display="block" gutterBottom sx={{ fontSize: '2rem' }}>
                      LEGAL DISCLAIMER / AVIS JURIDIQUE
                    </Typography>
                    <Typography variant="body1" display="block" sx={{ fontSize: '1.5rem', lineHeight: 1.6 }}>
                      This mediation is an internal attempt to resolve disputes and does not constitute a legal proceeding. 
                      Cette médiation est une tentative interne de résolution des litiges et ne constitue pas une procédure judiciaire.
                    </Typography>
                  </Alert>
                </Grid>

                {/* Mediation Chat History */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Mediation Conversation</Typography>
                  <Box sx={{ 
                    maxHeight: 300, 
                    overflowY: 'auto', 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    {/* Initial Report Message */}
                    <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                      <Paper sx={{ p: 1.5, bgcolor: 'primary.light', color: 'white', borderRadius: '15px 15px 15px 2px' }}>
                        <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>Initial Report</Typography>
                        <Typography variant="body2">"{selectedReport.message.content}"</Typography>
                      </Paper>
                      <Typography variant="caption" color="textSecondary">{new Date(selectedReport.message.createdAt).toLocaleString()}</Typography>
                    </Box>

                    {/* Chat Responses */}
                    {linkedSupportTicket?.responses.filter(r => r.message.includes('[Mediation Chat]')).map((resp) => {
                      const msgContent = resp.message.replace('[Mediation Chat] ', '');
                      return (
                        <Box key={resp.id} sx={{ alignSelf: 'flex-end', maxWidth: '85%', textAlign: 'right' }}>
                          <Paper sx={{ p: 1.5, bgcolor: 'success.light', color: 'white', borderRadius: '15px 15px 2px 15px' }}>
                            <Typography variant="body2">{msgContent}</Typography>
                          </Paper>
                          <Typography variant="caption" color="textSecondary">{new Date(resp.createdAt).toLocaleString()} - {resp.authorName}</Typography>
                        </Box>
                      );
                    })}

                    {linkedSupportTicket?.status === 'resolved' && (
                      <Box sx={{ alignSelf: 'center', my: 1 }}>
                        <Chip label="Mediation Closed" size="small" variant="outlined" />
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Admin Input Area */}
                {linkedSupportTicket?.status !== 'resolved' && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Type a message to reporter's inbox..."
                        value={mediationMessage}
                        onChange={(e) => setMediationMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMediationMessage()}
                      />
                      <Button 
                        variant="contained" 
                        onClick={handleSendMediationMessage}
                        disabled={!mediationMessage.trim()}
                      >
                        Send
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReportDetailsOpen(false)}>Close</Button>
          {linkedSupportTicket?.status !== 'resolved' && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  displayEmpty
                  value=""
                  onChange={(e) => handleCloseMediation(e.target.value as string)}
                  renderValue={() => "Close Case with Message..."}
                >
                  <MenuItem value="Based on evidence, no violation was found. The case is now closed.">No Violation Found</MenuItem>
                  <MenuItem value="The reported user has been warned. Further violations will lead to suspension.">Warning Issued</MenuItem>
                  <MenuItem value="This matter is outside our jurisdiction. Please refer to legal authorities.">Refer to Authorities</MenuItem>
                  <MenuItem value="The issue has been resolved between parties. Case closed.">Resolved Friendly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Ticket #{selectedTicket?.ticketNumber}</Typography>
            <IconButton onClick={handleCloseDetails}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
                <Tab label="Details" />
                <Tab label="Conversation" icon={<Badge badgeContent={selectedTicket.responses.length} color="primary" />} iconPosition="end" />
                <Tab label="Actions" />
              </Tabs>

              {/* Details Tab */}
              {activeTab === 0 && (
                <Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Reporter</Typography>
                      <Typography variant="body1">{selectedTicket.reporterName}</Typography>
                      <Typography variant="body2" color="textSecondary">{selectedTicket.reporterEmail}</Typography>
                      <Typography variant="body2" color="textSecondary">{selectedTicket.reporterPhone}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Status & Priority</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip label={selectedTicket.status.replace('_', ' ')} color={getStatusColor(selectedTicket.status)} />
                        <Chip label={selectedTicket.priority} color={getPriorityColor(selectedTicket.priority)} />
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="textSecondary">Category</Typography>
                    <Typography variant="body1">{selectedTicket.category}</Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="textSecondary">Subject</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedTicket.subject}</Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="textSecondary">Description</Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                      <Typography variant="body2">{selectedTicket.description}</Typography>
                    </Paper>
                  </Box>

                  {selectedTicket.reportedUserName && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" color="textSecondary">Reported User</Typography>
                      <Typography variant="body1">{selectedTicket.reportedUserName}</Typography>
                    </Box>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="textSecondary">Assigned To</Typography>
                    <Typography variant="body1">
                      {selectedTicket.assignedToName || 'Unassigned'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Created</Typography>
                      <Typography variant="body2">{new Date(selectedTicket.createdAt).toLocaleString()}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Last Updated</Typography>
                      <Typography variant="body2">{new Date(selectedTicket.updatedAt).toLocaleString()}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Conversation Tab */}
              {activeTab === 1 && (
                <Box>
                  <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 3 }}>
                    {selectedTicket.responses.map((response) => (
                      <Paper
                        key={response.id}
                        sx={{
                          p: 2,
                          mb: 2,
                          bgcolor: response.isInternal ? 'warning.light' : 'grey.50'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {response.authorName}
                            {response.isInternal && (
                              <Chip label="Internal Note" size="small" color="warning" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(response.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{response.message}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <TextField
                    label="Your Response"
                    fullWidth
                    multiline
                    rows={4}
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Type your response here..."
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <FormControl size="small">
                      <Select
                        value={isInternalNote ? 'internal' : 'public'}
                        onChange={(e) => setIsInternalNote(e.target.value === 'internal')}
                      >
                        <MenuItem value="public">Public Response</MenuItem>
                        <MenuItem value="internal">Internal Note</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      startIcon={<Send />}
                      onClick={handleSendResponse}
                      disabled={!responseMessage.trim()}
                    >
                      Send
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Actions Tab */}
              {activeTab === 2 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Change Status</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <Button
                      variant={selectedTicket.status === 'in_progress' ? 'contained' : 'outlined'}
                      onClick={() => handleUpdateStatus('in_progress')}
                    >
                      In Progress
                    </Button>
                    <Button
                      variant={selectedTicket.status === 'resolved' ? 'contained' : 'outlined'}
                      onClick={() => handleUpdateStatus('resolved')}
                    >
                      Resolved
                    </Button>
                    <Button
                      variant={selectedTicket.status === 'closed' ? 'contained' : 'outlined'}
                      onClick={() => handleUpdateStatus('closed')}
                    >
                      Close
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>Change Priority</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <Button
                      variant={selectedTicket.priority === 'low' ? 'contained' : 'outlined'}
                      color="success"
                      onClick={() => handleUpdatePriority('low')}
                    >
                      Low
                    </Button>
                    <Button
                      variant={selectedTicket.priority === 'medium' ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => handleUpdatePriority('medium')}
                    >
                      Medium
                    </Button>
                    <Button
                      variant={selectedTicket.priority === 'high' ? 'contained' : 'outlined'}
                      color="warning"
                      onClick={() => handleUpdatePriority('high')}
                    >
                      High
                    </Button>
                    <Button
                      variant={selectedTicket.priority === 'urgent' ? 'contained' : 'outlined'}
                      color="error"
                      onClick={() => handleUpdatePriority('urgent')}
                    >
                      Urgent
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>Assignment</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Assignment />}
                    onClick={() => setAssignmentOpen(true)}
                  >
                    {selectedTicket.assignedToName ? 'Reassign Ticket' : 'Assign Ticket'}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={assignmentOpen} onClose={() => setAssignmentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Ticket</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Support Agent</InputLabel>
            <Select
              value={assignTo}
              label="Support Agent"
              onChange={(e) => setAssignTo(e.target.value)}
            >
              {supportAgents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.preferredName || agent.firstName} {agent.lastName ? agent.lastName.charAt(0) + '.' : ''} ({agent.activeTickets || 0} active tickets)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignTicket}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, show: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportPage;
